var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Coords } from '../../model/Coords';
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import '../form/FormStepper.js';
import '../text/BasicNote.js';
import '../div/BasicSeparator.js';
/** Fresh, collision-free id for a POI created through the form. Each `add`
 *  operation must carry its own snapshot id: hosts (e.g. workflow-strategy)
 *  resolve an operation back to its POI by this id when the row has no
 *  persisted `poi_id`, so a shared constant makes every add on a network
 *  resolve to the first one. Prefer `crypto.randomUUID()`, falling back to a
 *  time+random string where it is unavailable. */
function newPoiId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `poi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
let AddEditPoiForm = class AddEditPoiForm extends LitElement {
    constructor() {
        super(...arguments);
        this.networkFields = [];
        this.poi = null;
        this.hideId = false;
        this.hideOpenDate = false;
        /** Current step: 1 = Identity, 2 = Properties. The network is chosen by
         *  the host before the form opens (e.g. a modal in workflow-strategy, or
         *  the already-selected network in workflow-network), so the form itself
         *  is a focused two-step POI editor. */
        this.step = 1;
        this.formData = this.poi !== null
            ? {
                id: this.poi.id,
                name: this.poi.name,
                description: this.poi.description || null,
                address: this.poi.address || null,
                postal_code: this.poi.postal_code || null,
                city: this.poi.city || null,
                open_date: this.poi.open_date || null,
                longitude: this.poi.longitude || null,
                latitude: this.poi.latitude || null,
                properties: this.poi.properties || null,
            }
            : {
                id: newPoiId(),
                name: '',
                description: null,
                address: null,
                postal_code: null,
                city: null,
                open_date: null,
                longitude: null,
                latitude: null,
                properties: {},
            };
        /** Signature of the address that currently matches the form's
         *  coordinates. The blur handler only geocodes when the typed address
         *  *differs* from this, so re-blurring an unchanged (e.g. pre-filled)
         *  address never re-fires — and tabbing through an existing POI never
         *  relocates it. Captured on load and after each geocode dispatch. */
        this._lastGeocodedAddress = null;
        /** Identity step — the user finished editing one of the postal-address
         *  fields (address / city / postal code) and left it. When the combined
         *  address actually changed since it last matched the coordinates, ask
         *  the host to geocode it (the host owns the API call + token) and push
         *  the resulting coords back through `setCoords`, which moves the pin.
         *  The signature guard means an unchanged address never re-fires, so
         *  tabbing through an existing POI can't relocate it. */
        this.handleAddressFieldBlur = () => {
            const address = (this.formData.address ?? '').toString().trim();
            if (!address)
                return;
            const signature = this._addressSignature();
            if (signature === this._lastGeocodedAddress)
                return;
            this._lastGeocodedAddress = signature;
            this.dispatchEvent(new CustomEvent('geocode-address', {
                detail: {
                    address,
                    city: this.formData.city ?? null,
                    postal_code: this.formData.postal_code ?? null,
                },
                bubbles: true,
                composed: true,
            }));
        };
        this._onCancel = () => {
            this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
        };
    }
    firstUpdated() {
        this._lastGeocodedAddress = this._addressSignature();
    }
    updated(changedProps) {
        if (changedProps.has('poi')) {
            this.formData = this.poi
                ? {
                    id: this.poi.id,
                    name: this.poi.name,
                    description: this.poi.description || null,
                    address: this.poi.address || null,
                    postal_code: this.poi.postal_code || null,
                    city: this.poi.city || null,
                    open_date: this.poi.open_date || null,
                    longitude: this.poi.longitude || null,
                    latitude: this.poi.latitude || null,
                    properties: this.poi.properties || {},
                }
                : {
                    id: newPoiId(),
                    name: '',
                    description: null,
                    address: null,
                    postal_code: null,
                    city: null,
                    open_date: null,
                    longitude: null,
                    latitude: null,
                    properties: {},
                };
            // New POI loaded → reset the baseline to its address so the first
            // blur doesn't geocode an untouched address.
            this._lastGeocodedAddress = this._addressSignature();
        }
    }
    /** Stable key built from the postal-address fields, used to detect a
     *  genuine address change between blurs. */
    _addressSignature() {
        return [this.formData.address, this.formData.postal_code, this.formData.city]
            .map((v) => (v ?? '').toString().trim())
            .join('|');
    }
    getFormData() {
        return this.formData;
    }
    mapClick(coords) {
        if (this.step === 1 && coords) {
            const { longitude, latitude } = coords;
            this.formData = { ...this.formData, longitude, latitude };
            this.dispatchEvent(new CustomEvent('add-marker', {
                detail: coords,
                bubbles: true,
                composed: true,
            }));
        }
    }
    /** Set the POI's coordinates regardless of which step the user is on.
     *  Use this when the host already knows the gesture is unambiguous
     *  (e.g. dragging an existing on-map pin). Map clicks should still go
     *  through `mapClick`, which gates on the address/coords step. */
    setCoords(coords) {
        if (!coords)
            return;
        const { longitude, latitude } = coords;
        this.formData = { ...this.formData, longitude, latitude };
        this.dispatchEvent(new CustomEvent('add-marker', {
            detail: coords,
            bubbles: true,
            composed: true,
        }));
    }
    handleInputChange(e) {
        const target = e.target;
        let value = target.value;
        if (target.type === 'number' && target instanceof HTMLInputElement) {
            value = isNaN(target.valueAsNumber) ? null : target.valueAsNumber;
        }
        this.formData = { ...this.formData, [target.id]: value };
        if (target.id === 'latitude' || target.id === 'longitude') {
            const latitude = target.id === 'latitude'
                ? value
                : this.formData.latitude;
            const longitude = target.id === 'longitude'
                ? value
                : this.formData.longitude;
            if (latitude !== null && longitude !== null) {
                this.dispatchEvent(new CustomEvent('add-marker', {
                    detail: { latitude, longitude },
                    bubbles: true,
                    composed: true,
                }));
            }
        }
    }
    nextStep() {
        if (this.step < 2)
            this.step++;
    }
    prevStep() {
        if (this.step === 2)
            this.step = 1;
    }
    handleSubmit() {
        this.dispatchEvent(new CustomEvent('submit-poi', {
            detail: {
                poi_id: this.poi ? this.poi.poi_id : null,
                create: this.poi ? null : this.formData,
                update: this.poi ? this.formData : null,
            },
            bubbles: true,
            composed: true,
        }));
    }
    handleCleanPosition() {
        this.formData = {
            ...this.formData,
            address: null,
            city: null,
            postal_code: null,
            latitude: null,
            longitude: null,
        };
        // Position wiped → let a re-typed identical address geocode again.
        this._lastGeocodedAddress = null;
    }
    /** Clear only the postal address fields (address / city / postal_code),
     *  keeping latitude / longitude intact. Used by hosts that need to drop
     *  a stale address after the user moves the POI elsewhere on the map. */
    resetAddress() {
        this.formData = {
            ...this.formData,
            address: null,
            city: null,
            postal_code: null,
        };
        // Address dropped → a fresh address (even the same text) should geocode.
        this._lastGeocodedAddress = null;
    }
    // ─── Step renders ────────────────────────────────────────────
    /** Step 1 — Identity (name + address + city/postal + lng/lat). */
    renderIdentityStep() {
        return html `
      ${this.hideId
            ? ''
            : html `<div class="form-group">
            <label for="id"
              >${msg('Unique Identifier', {
                id: 'poi.form.uniqueId',
                desc: 'Unique ID label',
            })}</label
            >
            <input-text
              id="id"
              type="text"
              .value=${this.formData.id}
              @input-text=${this.handleInputChange}
            />
          </div>`}
      <div class="form-group">
        <label for="name"
          >${msg('POI Name', {
            id: 'poi.form.name',
            desc: 'POI name label',
        })}<span class="required">*</span></label
        >
        <input-text
          id="name"
          type="text"
          .value=${this.formData.name}
          @input-text=${this.handleInputChange}
        />
      </div>
      <div class="form-group">
        <label for="description"
          >${msg('Description', {
            id: 'poi.form.description',
            desc: 'Description label',
        })}</label
        >
        <input-text-area
          id="description"
          .value=${this.formData.description ?? ''}
          @input-text-area=${this.handleInputChange}
        ></input-text-area>
      </div>
      <basic-separator size="small"></basic-separator>
      <div class="form-group">
        <label for="address"
          >${msg('Address', {
            id: 'poi.form.address',
            desc: 'Address label',
        })}</label
        >
        <input-text
          id="address"
          type="text"
          .value=${this.formData.address ?? ''}
          @input-text=${this.handleInputChange}
          @focusout=${this.handleAddressFieldBlur}
        />
      </div>
      <div class="form-row">
        <div class="form-group">
          <label for="city"
            >${msg('City', {
            id: 'poi.form.city',
            desc: 'City label',
        })}</label
          >
          <input-text
            id="city"
            type="text"
            .value=${this.formData.city ?? ''}
            @input-text=${this.handleInputChange}
            @focusout=${this.handleAddressFieldBlur}
          />
        </div>
        <div class="form-group">
          <label for="postal_code"
            >${msg('Postal Code', {
            id: 'poi.form.postalCode',
            desc: 'Postal code label',
        })}</label
          >
          <input-text
            id="postal_code"
            type="text"
            .value=${this.formData.postal_code ?? ''}
            @input-text=${this.handleInputChange}
            @focusout=${this.handleAddressFieldBlur}
          />
        </div>
      </div>
      <basic-separator size="small"></basic-separator>
      <div class="form-row">
        <div class="form-group">
          <label for="longitude"
            >${msg('Longitude', {
            id: 'poi.form.longitude',
            desc: 'Longitude label',
        })}</label
          >
          <input-number
            id="longitude"
            .value=${this.formData.longitude ?? ''}
            step="any"
            @input-number=${this.handleInputChange}
          ></input-number>
        </div>
        <div class="form-group">
          <label for="latitude"
            >${msg('Latitude', {
            id: 'poi.form.latitude',
            desc: 'Latitude label',
        })}</label
          >
          <input-number
            id="latitude"
            .value=${this.formData.latitude ?? ''}
            step="any"
            @input-number=${this.handleInputChange}
          ></input-number>
        </div>
      </div>
      <basic-note variant="primary" size="small">
        ${msg('Fill in the address, the coordinates, or both. Whichever you leave empty will be auto-filled by the geocoder.', {
            id: 'poi.form.identityHint',
            desc: 'Hint explaining that the missing half (address or coords) is auto-generated via geocoding',
        })}
      </basic-note>
      ${this.poi
            ? html `
            <div class="position-btn-row">
              <simple-button
                variant="secondary"
                size="small"
                @button-click=${this.handleCleanPosition}
                label=${msg('Reset Position', {
                id: 'poi.form.resetPosition',
                desc: 'Reset position button',
            })}
              ></simple-button>
            </div>
          `
            : ''}
    `;
    }
    /** Step 2 — Properties (open_date + custom network fields). */
    renderPropertiesStep() {
        return html `
      ${this.hideOpenDate
            ? ''
            : html `<div class="form-group">
            <label for="open_date"
              >${msg('Opening Date', {
                id: 'poi.form.openingDate',
                desc: 'Opening date label',
            })}</label
            >
            <input-date
              id="open_date"
              type="date"
              .value=${this.formData.open_date ?? ''}
              @input-date=${this.handleInputChange}
            />
          </div>`}
      ${this.networkFields.length > 0
            ? this.renderNetworkFieldInputs()
            : ''}
    `;
    }
    renderNetworkFieldInputs() {
        return this.networkFields
            .filter((field) => !field.native &&
            ['number', 'string', 'date', 'boolean'].includes(field.type) &&
            field.name &&
            field.alias)
            .map((field) => {
            const value = this.formData.properties?.[field.name] ?? '';
            let inputEl;
            if (field.type === 'string') {
                inputEl = html `
            <input-text
              id="property-${field.name}"
              .value=${value}
              @input-text=${(e) => {
                    const newProperties = { ...(this.formData.properties || {}) };
                    newProperties[field.name] = e.detail;
                    this.formData = { ...this.formData, properties: newProperties };
                }}
            />
          `;
            }
            else if (field.type === 'number') {
                inputEl = html `
            <input-number
              id="property-${field.name}"
              .value=${value}
              step="any"
              @input-number=${(e) => {
                    const newProperties = { ...(this.formData.properties || {}) };
                    newProperties[field.name] =
                        e.detail === '' ? null : parseFloat(e.detail);
                    this.formData = { ...this.formData, properties: newProperties };
                }}
            ></input-number>
          `;
            }
            else if (field.type === 'date') {
                inputEl = html `
            <input-date
              id="property-${field.name}"
              .value=${value}
              @input-date=${(e) => {
                    const newProperties = { ...(this.formData.properties || {}) };
                    newProperties[field.name] = e.detail;
                    this.formData = { ...this.formData, properties: newProperties };
                }}
            />
          `;
            }
            else if (field.type === 'boolean') {
                inputEl = html `
            <input
              id="property-${field.name}"
              type="checkbox"
              .checked=${!!value}
              @change=${(e) => {
                    const target = e.target;
                    const newProperties = { ...(this.formData.properties || {}) };
                    newProperties[field.name] = target.checked;
                    this.formData = { ...this.formData, properties: newProperties };
                }}
            />
          `;
            }
            return html `
          <div class="form-group">
            <label for="property-${field.name}">${field.alias}</label>
            ${inputEl}
          </div>
        `;
        });
    }
    // ─── Stepper / panel helpers ─────────────────────────────────
    renderStepper() {
        const steps = [
            msg('Identity', {
                id: 'poi.form.step.identity',
                desc: 'Stepper label — POI identity',
            }),
            msg('Properties', {
                id: 'poi.form.step.properties',
                desc: 'Stepper label — POI properties',
            }),
        ];
        return html `
      <form-stepper
        .currentStep=${this.step}
        .steps=${steps}
      ></form-stepper>
    `;
    }
    /** Whether the user can advance from the current step. Drives the
     *  primary action button's `disabled` state. */
    get _canAdvance() {
        if (this.step === 1) {
            const name = (this.formData.name ?? '').toString().trim();
            const hasAddress = !!(this.formData.address ?? '').toString().trim() ||
                !!(this.formData.city ?? '').toString().trim() ||
                (this.formData.latitude != null && this.formData.longitude != null);
            return name.length > 0 && hasAddress;
        }
        return true;
    }
    renderActions() {
        const canBack = this.step > 1;
        const isLast = this.step === 2;
        const leftLabel = canBack
            ? msg('Back', {
                id: 'poi.form.action.back',
                desc: 'Step-back button',
            })
            : msg('Cancel', {
                id: 'poi.form.action.cancel',
                desc: 'Cancel button',
            });
        const rightLabel = isLast
            ? msg('Save', {
                id: 'poi.form.action.save',
                desc: 'Submit / save button on the last step',
            })
            : msg('Next', {
                id: 'poi.form.action.next',
                desc: 'Step-next button',
            });
        return html `
      <div class="form-actions">
        <simple-button
          variant="secondary"
          size="small"
          .label=${leftLabel}
          @button-click=${canBack ? this.prevStep : this._onCancel}
        ></simple-button>
        <div class="actions-right">
          <simple-button
            variant="primary"
            size="small"
            ?disabled=${!this._canAdvance}
            .label=${rightLabel}
            @button-click=${isLast ? this.handleSubmit : this.nextStep}
          ></simple-button>
        </div>
      </div>
    `;
    }
    // ─── Top-level renders ───────────────────────────────────────
    renderBody() {
        if (this.step === 1)
            return this.renderIdentityStep();
        return this.renderPropertiesStep();
    }
    render() {
        return html `
      ${this.renderStepper()} ${this.renderBody()} ${this.renderActions()}
    `;
    }
};
AddEditPoiForm.styles = css `
    :host {
      display: block;
      font-family: inherit;
      color: #333;
    }

    form-stepper {
      display: block;
      margin-bottom: 12px;
    }

    /* Generic form fields */
    .form-group {
      margin-bottom: 14px;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .form-row .form-group {
      margin-bottom: 0;
    }
    label {
      display: block;
      margin-bottom: 5px;
      font-size: 12px;
      font-weight: 600;
      color: #4a5868;
    }
    .required {
      color: #c0392b;
      margin-left: 2px;
    }
    .form-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      margin-top: 14px;
      padding-top: 12px;
    }
    .form-actions .actions-right {
      display: flex;
      gap: 8px;
    }

    basic-note {
      display: block;
    }
    .position-btn-row {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
      margin-bottom: 8px;
    }
  `;
__decorate([
    property({ type: Object })
], AddEditPoiForm.prototype, "networkFields", void 0);
__decorate([
    property({ type: Object })
], AddEditPoiForm.prototype, "poi", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-id' })
], AddEditPoiForm.prototype, "hideId", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-open-date' })
], AddEditPoiForm.prototype, "hideOpenDate", void 0);
__decorate([
    state()
], AddEditPoiForm.prototype, "step", void 0);
__decorate([
    state()
], AddEditPoiForm.prototype, "formData", void 0);
AddEditPoiForm = __decorate([
    localized(),
    customElement('add-edit-poi-form')
], AddEditPoiForm);
export { AddEditPoiForm };
//# sourceMappingURL=AddEditPoiForm.js.map